import React, { useMemo, useRef, useState } from "react";
import ProTable from "@components/ResponsiveProTable";

const widthKeyForCol = (col, index) =>
  col.dataIndex != null
    ? col.dataIndex
    : col.key != null
      ? col.key
      : `__col_${index}`;

/** 从列头右缘向内命中才触发调整，不改动 title DOM，避免打乱 ProTable 排序/筛选 flex 布局 */
const RESIZE_ZONE_PX = 10;
const GUIDE_CLASS = "table-col-resize-guide";

const withResizableColumns = (WrappedTableComponent) => {
  function ResizableColumnsComponent(props) {
    const { columns: incomingColumns = [], ...rest } = props;
    const [columnWidths, setColumnWidths] = useState({});
    const dragRef = useRef(null);

    const columnsWithResizer = useMemo(
      () =>
        incomingColumns.map((col, index) => {
          if (col.hideInTable) {
            return { ...col };
          }
          const wKey = widthKeyForCol(col, index);
          const mergedWidth = columnWidths[wKey] ?? col.width;

          const onHeaderCell = (headerCol) => {
            const prev =
              typeof col.onHeaderCell === "function"
                ? col.onHeaderCell(headerCol)
                : col.onHeaderCell || {};
            const prevClass = prev.className;
            const prevOnMouseDown = prev.onMouseDown;

            return {
              ...prev,
              className: [prevClass, "table-col-resize-edge"].filter(Boolean).join(" "),
              onMouseDown: (e) => {
                if (e.button !== 0) {
                  prevOnMouseDown?.(e);
                  return;
                }
                const th = e.currentTarget;
                const rect = th.getBoundingClientRect();
                const fromRight = rect.right - e.clientX;
                if (fromRight > RESIZE_ZONE_PX) {
                  prevOnMouseDown?.(e);
                  return;
                }

                e.preventDefault();
                e.stopPropagation();

                const tableEl =
                  th.closest(".ant-table") ||
                  th.closest("[class*='ant-table']");
                const theadCells = tableEl
                  ? Array.from(tableEl.querySelectorAll(".ant-table-thead th"))
                  : [];

                const startWidth = rect.width;
                dragRef.current = {
                  widthKey: wKey,
                  startX: e.clientX,
                  startWidth,
                  lastWidth: startWidth,
                  theadCells,
                  th,
                };

                th.classList.add("table-col-resize-active-th");
                document.body.classList.add("table-col-resize-dragging");

                const guide = document.createElement("div");
                guide.className = GUIDE_CLASS;
                guide.style.left = `${e.clientX}px`;
                document.body.appendChild(guide);

                document.body.style.userSelect = "none";
                theadCells.forEach((cell) => {
                  cell.style.pointerEvents = "none";
                });

                const onMove = (ev) => {
                  const d = dragRef.current;
                  if (!d) return;
                  ev.preventDefault();
                  guide.style.left = `${ev.clientX}px`;
                  const next = Math.max(48, d.startWidth + (ev.clientX - d.startX));
                  d.lastWidth = next;
                  if (d.th) {
                    d.th.style.width = `${next}px`;
                    d.th.style.minWidth = `${next}px`;
                  }
                };

                let blurHandler;
                const onUp = () => {
                  document.removeEventListener("mousemove", onMove, true);
                  document.removeEventListener("mouseup", onUp, true);
                  if (blurHandler) {
                    window.removeEventListener("blur", blurHandler);
                    blurHandler = null;
                  }

                  if (guide.parentNode) {
                    guide.remove();
                  }
                  document.body.classList.remove("table-col-resize-dragging");
                  document.body.style.userSelect = "";

                  const d = dragRef.current;
                  dragRef.current = null;

                  if (!d) return;

                  const finalW = d.lastWidth ?? d.startWidth;
                  (d.theadCells || []).forEach((cell) => {
                    cell.style.pointerEvents = "";
                  });

                  if (d.th) {
                    d.th.classList.remove("table-col-resize-active-th");
                    d.th.style.width = "";
                    d.th.style.minWidth = "";
                    d.th.classList.add("table-col-resize-released");
                    const thEl = d.th;
                    window.setTimeout(() => {
                      thEl.classList.remove("table-col-resize-released");
                    }, 220);
                  }

                  setColumnWidths((prev) => {
                    if (prev[d.widthKey] === finalW) return prev;
                    return { ...prev, [d.widthKey]: finalW };
                  });
                };

                blurHandler = () => onUp();
                window.addEventListener("blur", blurHandler);
                document.addEventListener("mousemove", onMove, true);
                document.addEventListener("mouseup", onUp, true);
              },
            };
          };

          return {
            ...col,
            width: mergedWidth,
            onHeaderCell,
          };
        }),
      [incomingColumns, columnWidths]
    );

    return <WrappedTableComponent {...rest} columns={columnsWithResizer} />;
  }

  return ResizableColumnsComponent;
};

const ResizableProTable = withResizableColumns(ProTable);

export default ResizableProTable;
